import { useState, useEffect } from "react";
import { Vendor } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, X, Building2, Phone, Mail, MapPin, CreditCard, FileText, Award } from "lucide-react";
import { toast } from "sonner";

interface VendorFormProps {
  vendor?: Vendor;
  onSave: (vendorData: Partial<Vendor>) => Promise<boolean>;
  onCancel: () => void;
}

export const VendorForm = ({ vendor, onSave, onCancel }: VendorFormProps) => {
  const { state } = useProcurement();
  const [activeTab, setActiveTab] = useState("basic");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Basic Information
  const [formData, setFormData] = useState({
    vendorCode: vendor?.vendorCode || "",
    name: vendor?.name || "",
    contactDetails: {
      email: vendor?.contactDetails.email || "",
      phone: vendor?.contactDetails.phone || "",
      website: vendor?.contactDetails.website || "",
      address: {
        street: vendor?.contactDetails.address.street || "",
        city: vendor?.contactDetails.address.city || "",
        state: vendor?.contactDetails.address.state || "",
        zip: vendor?.contactDetails.address.zip || "",
        country: vendor?.contactDetails.address.country || "USA"
      },
      primaryContact: {
        name: vendor?.contactDetails.primaryContact.name || "",
        title: vendor?.contactDetails.primaryContact.title || "",
        email: vendor?.contactDetails.primaryContact.email || "",
        phone: vendor?.contactDetails.primaryContact.phone || ""
      }
    },
    paymentTerms: vendor?.paymentTerms || "Net 30",
    isActive: vendor?.isActive ?? true,
  });

  // Certifications
  const [certifications, setCertifications] = useState<string[]>(
    vendor?.certifications || []
  );

  // Bank Details
  const [bankDetails, setBankDetails] = useState({
    bankName: vendor?.bankDetails?.bankName || "",
    accountNumber: vendor?.bankDetails?.accountNumber || "",
    routingNumber: vendor?.bankDetails?.routingNumber || ""
  });

  // Tax Information
  const [taxInfo, setTaxInfo] = useState({
    taxId: vendor?.taxInfo.taxId || "",
    gstNumber: vendor?.taxInfo.gstNumber || ""
  });

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Payment terms options
  const paymentTermsOptions = [
    'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90',
    'Due on Receipt', '2/10 Net 30', '1/10 Net 30',
    'Cash on Delivery', 'Prepayment Required'
  ];

  // Countries list
  const countries = [
    'USA', 'Canada', 'Mexico', 'United Kingdom', 'Germany', 'France',
    'Italy', 'Spain', 'Japan', 'China', 'India', 'Australia'
  ];

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.vendorCode.trim()) {
      newErrors.vendorCode = "Vendor Code is required";
    } else if (state.vendors.some(v => v.vendorCode === formData.vendorCode && v.id !== vendor?.id)) {
      newErrors.vendorCode = "Vendor Code already exists";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Vendor Name is required";
    }

    if (!formData.contactDetails.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactDetails.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.contactDetails.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.contactDetails.address.street.trim()) {
      newErrors.street = "Street address is required";
    }

    if (!formData.contactDetails.address.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.contactDetails.address.state.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.contactDetails.address.zip.trim()) {
      newErrors.zip = "ZIP code is required";
    }

    if (!formData.contactDetails.primaryContact.name.trim()) {
      newErrors.primaryContactName = "Primary contact name is required";
    }

    if (!formData.contactDetails.primaryContact.email.trim()) {
      newErrors.primaryContactEmail = "Primary contact email is required";
    }

    if (!taxInfo.taxId.trim()) {
      newErrors.taxId = "Tax ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);

    const vendorData: Partial<Vendor> = {
      ...formData,
      certifications,
      bankDetails: bankDetails.bankName ? bankDetails : undefined,
      taxInfo,
      linkedSKUs: vendor?.linkedSKUs || [],
      performanceRating: vendor?.performanceRating || 0,
      deliveryMetrics: vendor?.deliveryMetrics || {
        totalOrders: 0,
        onTimeDeliveries: 0,
        qualityScore: 0,
        responseTime: 24
      },
      updatedAt: new Date().toISOString(),
      ...(vendor ? {} : { 
        createdAt: new Date().toISOString()
      })
    };

    const success = await onSave(vendorData);
    setIsSubmitting(false);

    if (success) {
      toast.success(vendor ? "Vendor updated successfully" : "Vendor created successfully");
    }
  };

  // Add certification
  const addCertification = () => {
    setCertifications([...certifications, ""]);
  };

  // Remove certification
  const removeCertification = (index: number) => {
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  // Update certification
  const updateCertification = (index: number, value: string) => {
    const updated = [...certifications];
    updated[index] = value;
    setCertifications(updated);
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {vendor ? "Edit Vendor" : "Add New Vendor"}
          </DialogTitle>
          <DialogDescription>
            {vendor ? "Update vendor information and settings" : "Add a new vendor to your supplier network"}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Basic Info
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Contact
            </TabsTrigger>
            <TabsTrigger value="financial" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Financial
            </TabsTrigger>
            <TabsTrigger value="certifications" className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              Certifications
            </TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendorCode">Vendor Code *</Label>
                <Input
                  id="vendorCode"
                  value={formData.vendorCode}
                  onChange={(e) => setFormData({ ...formData, vendorCode: e.target.value })}
                  placeholder="VEN-001"
                  className={errors.vendorCode ? "border-red-500" : ""}
                />
                {errors.vendorCode && <p className="text-sm text-red-500">{errors.vendorCode}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Vendor Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Company Name"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.contactDetails.email}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    contactDetails: { ...formData.contactDetails, email: e.target.value }
                  })}
                  placeholder="contact@company.com"
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.contactDetails.phone}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    contactDetails: { ...formData.contactDetails, phone: e.target.value }
                  })}
                  placeholder="+1 (555) 123-4567"
                  className={errors.phone ? "border-red-500" : ""}
                />
                {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.contactDetails.website}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    contactDetails: { ...formData.contactDetails, website: e.target.value }
                  })}
                  placeholder="https://company.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentTerms">Payment Terms</Label>
                <Select 
                  value={formData.paymentTerms} 
                  onValueChange={(value) => setFormData({ ...formData, paymentTerms: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentTermsOptions.map(term => (
                      <SelectItem key={term} value={term}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2 md:col-span-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive">Active Vendor</Label>
              </div>
            </div>
          </TabsContent>

          {/* Contact Information Tab */}
          <TabsContent value="contact" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Address Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    value={formData.contactDetails.address.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      contactDetails: {
                        ...formData.contactDetails,
                        address: { ...formData.contactDetails.address, street: e.target.value }
                      }
                    })}
                    placeholder="123 Main Street"
                    className={errors.street ? "border-red-500" : ""}
                  />
                  {errors.street && <p className="text-sm text-red-500">{errors.street}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.contactDetails.address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          address: { ...formData.contactDetails.address, city: e.target.value }
                        }
                      })}
                      placeholder="City"
                      className={errors.city ? "border-red-500" : ""}
                    />
                    {errors.city && <p className="text-sm text-red-500">{errors.city}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.contactDetails.address.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          address: { ...formData.contactDetails.address, state: e.target.value }
                        }
                      })}
                      placeholder="State"
                      className={errors.state ? "border-red-500" : ""}
                    />
                    {errors.state && <p className="text-sm text-red-500">{errors.state}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input
                      id="zip"
                      value={formData.contactDetails.address.zip}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          address: { ...formData.contactDetails.address, zip: e.target.value }
                        }
                      })}
                      placeholder="12345"
                      className={errors.zip ? "border-red-500" : ""}
                    />
                    {errors.zip && <p className="text-sm text-red-500">{errors.zip}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select 
                    value={formData.contactDetails.address.country} 
                    onValueChange={(value) => setFormData({
                      ...formData,
                      contactDetails: {
                        ...formData.contactDetails,
                        address: { ...formData.contactDetails.address, country: value }
                      }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Primary Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primaryContactName">Contact Name *</Label>
                    <Input
                      id="primaryContactName"
                      value={formData.contactDetails.primaryContact.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          primaryContact: { ...formData.contactDetails.primaryContact, name: e.target.value }
                        }
                      })}
                      placeholder="John Doe"
                      className={errors.primaryContactName ? "border-red-500" : ""}
                    />
                    {errors.primaryContactName && <p className="text-sm text-red-500">{errors.primaryContactName}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primaryContactTitle">Title</Label>
                    <Input
                      id="primaryContactTitle"
                      value={formData.contactDetails.primaryContact.title}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          primaryContact: { ...formData.contactDetails.primaryContact, title: e.target.value }
                        }
                      })}
                      placeholder="Sales Manager"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primaryContactEmail">Contact Email *</Label>
                    <Input
                      id="primaryContactEmail"
                      type="email"
                      value={formData.contactDetails.primaryContact.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          primaryContact: { ...formData.contactDetails.primaryContact, email: e.target.value }
                        }
                      })}
                      placeholder="john.doe@company.com"
                      className={errors.primaryContactEmail ? "border-red-500" : ""}
                    />
                    {errors.primaryContactEmail && <p className="text-sm text-red-500">{errors.primaryContactEmail}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primaryContactPhone">Contact Phone</Label>
                    <Input
                      id="primaryContactPhone"
                      value={formData.contactDetails.primaryContact.phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactDetails: {
                          ...formData.contactDetails,
                          primaryContact: { ...formData.contactDetails.primaryContact, phone: e.target.value }
                        }
                      })}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Information Tab */}
          <TabsContent value="financial" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tax Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="taxId">Tax ID *</Label>
                    <Input
                      id="taxId"
                      value={taxInfo.taxId}
                      onChange={(e) => setTaxInfo({ ...taxInfo, taxId: e.target.value })}
                      placeholder="12-3456789"
                      className={errors.taxId ? "border-red-500" : ""}
                    />
                    {errors.taxId && <p className="text-sm text-red-500">{errors.taxId}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gstNumber">GST Number</Label>
                    <Input
                      id="gstNumber"
                      value={taxInfo.gstNumber}
                      onChange={(e) => setTaxInfo({ ...taxInfo, gstNumber: e.target.value })}
                      placeholder="GST123456789"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Bank Details (Optional)</CardTitle>
                <CardDescription>For direct payment processing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input
                    id="bankName"
                    value={bankDetails.bankName}
                    onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                    placeholder="First National Bank"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      value={bankDetails.accountNumber}
                      onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                      placeholder="1234567890"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="routingNumber">Routing Number</Label>
                    <Input
                      id="routingNumber"
                      value={bankDetails.routingNumber}
                      onChange={(e) => setBankDetails({ ...bankDetails, routingNumber: e.target.value })}
                      placeholder="123456789"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certifications Tab */}
          <TabsContent value="certifications" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Certifications & Standards</CardTitle>
                    <CardDescription>Industry certifications and quality standards</CardDescription>
                  </div>
                  <Button onClick={addCertification} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Certification
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Input
                        value={cert}
                        onChange={(e) => updateCertification(index, e.target.value)}
                        placeholder="ISO 9001, ISO 14001, etc."
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCertification(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  {certifications.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No certifications added yet</p>
                      <p className="text-sm">Add industry certifications and quality standards</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : vendor ? "Update Vendor" : "Create Vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
